import api, { generateRequestHeader } from './api'
import { JIRA_COPY } from '@/utils/recruiterQaJira'

// Recruiter Q&A ("לשאול את קלאוד") — Admin::V1 endpoints. Identity is decided
// server-side from the JWT; nothing here sends an admin id.
const BASE = '/admin/v1/recruiter_questions'

const cfg = (extra = {}) => ({ ...extra, headers: generateRequestHeader('admin') })

export const ERROR_MESSAGES = {
  question_invalid: 'השאלה ריקה או ארוכה מדי (עד 4000 תווים)',
  context_invalid: 'הקישור לעמוד לא תקין — נסי להסיר את התגית ולשלוח שוב',
  conflict: 'השאלה כבר נשלחה',
  network: 'אין חיבור לשרת כרגע, השאלה לא נשלחה. נסי שוב',
  forbidden: 'אין לך הרשאה לצפות בפניות של עובדות אחרות',
  rate_limited: minutes => 'הגעת למגבלת השאלות לשעה, נסי שוב בעוד ' + minutes + ' דקות',
  screenshot_invalid: 'צילום המסך לא התקבל — אפשר לצרף קובץ תמונה (PNG, JPG, GIF או WEBP) עד 5MB, או למחוק אותו ולשלוח בלעדיו'
}

/**
 * Normalise an axios error into { kind, status, code, field, retryAfterS, message }.
 * kind ∈ feature_off | unauthorized | forbidden | not_found | question_invalid | context_invalid |
 *        conflict | rate_limited | network | other
 * 403 {"error":"forbidden"} (supervisor view, non-supervisor) -> 'forbidden'.
 * A 404 is 'feature_off' ONLY when the body says so; a plain 404 is 'not_found'.
 */
export const mapError = err => {
  const response = err && err.response
  if (!response) {
    return { kind: 'network', status: 0, message: ERROR_MESSAGES.network }
  }
  const status = response.status
  const body = (response.data && typeof response.data === 'object') ? response.data : {}
  const code = body.error
  const base = { status, code, field: body.field }
  if (status === 401) return { ...base, kind: 'unauthorized', message: '' }
  if (status === 403 && code === 'forbidden') {
    return { ...base, kind: 'forbidden', message: ERROR_MESSAGES.forbidden }
  }
  if (status === 404) {
    return code === 'feature_off'
      ? { ...base, kind: 'feature_off', message: '' }
      : { ...base, kind: 'not_found', message: '' }
  }
  if (status === 409) return { ...base, kind: 'conflict', message: ERROR_MESSAGES.conflict }
  if (status === 422 && code === 'context_invalid') {
    return { ...base, kind: 'context_invalid', message: ERROR_MESSAGES.context_invalid }
  }
  if (status === 422 && code === 'screenshot_invalid') {
    return { ...base, kind: 'screenshot_invalid', message: ERROR_MESSAGES.screenshot_invalid }
  }
  if (status === 422 && code === 'question_invalid') {
    return { ...base, kind: 'question_invalid', message: ERROR_MESSAGES.question_invalid }
  }
  if (status === 429) {
    const retryAfterS = Number(body.retry_after_s) > 0 ? Number(body.retry_after_s) : 60
    const minutes = Math.max(1, Math.ceil(retryAfterS / 60))
    return { ...base, kind: 'rate_limited', retryAfterS, message: ERROR_MESSAGES.rate_limited(minutes) }
  }
  if (status >= 500) return { ...base, kind: 'network', message: ERROR_MESSAGES.network }
  return { ...base, kind: 'other', message: ERROR_MESSAGES.network }
}

const unwrap = promise => promise.then(res => res.data)

export const getConfig = () => unwrap(api.get(BASE + '/config', cfg()))

export const getContext = ({ applicant_id, position_id, offer_id } = {}) => {
  const params = {}
  if (applicant_id) params.applicant_id = applicant_id
  if (position_id) params.position_id = position_id
  if (offer_id) params.offer_id = offer_id
  return unwrap(api.get(BASE + '/context', cfg({ params })))
}

export const listQuestions = ({ before_id } = {}) => {
  const params = before_id ? { before_id } : {}
  return unwrap(api.get(BASE, cfg({ params })))
}

// ids[]=1&ids[]=2 (Rails array params), max 20.
export const getUpdates = ids => {
  const query = (ids || []).slice(0, 20).map(id => 'ids[]=' + encodeURIComponent(id)).join('&')
  return unwrap(api.get(BASE + '/updates' + (query ? '?' + query : ''), cfg()))
}

export const getQuestion = id => unwrap(api.get(BASE + '/' + encodeURIComponent(id), cfg()))

export const createQuestion = ({ request_uuid, question, parent_id, context, screenshot }) => {
  const body = {
    request_uuid,
    question,
    parent_id: parent_id || null,
    context: context || {}
  }
  // Jira 8dce4932: optional { content_type, data_base64 } (see screenshotPayload).
  if (screenshot) body.screenshot = screenshot
  return unwrap(api.post(BASE, body, cfg()))
}

// ---- Screenshots (Jira 8dce4932). One image per question, sent with it. ----
export const SCREENSHOT_MAX_BYTES = 5 * 1024 * 1024

// File/Blob -> Promise<{ content_type, data_base64 }> for createQuestion.
export const screenshotPayload = file => new Promise((resolve, reject) => {
  if (!file) { resolve(null); return }
  const reader = new FileReader()
  reader.onload = () => {
    const result = String(reader.result || '')
    const comma = result.indexOf(',')
    resolve({ content_type: file.type || '', data_base64: comma >= 0 ? result.slice(comma + 1) : result })
  }
  reader.onerror = () => reject(reader.error || new Error('read_failed'))
  reader.readAsDataURL(file)
})

// { content_type, data_base64 } -> data: URL usable as an <img> src.
export const attachmentDataUrl = att =>
  att && att.data_base64 ? 'data:' + (att.content_type || 'image/png') + ';base64,' + att.data_base64 : ''

// GET /:id/attachment (own question) and /admin/questions/:id/attachment (supervisor).
export const getAttachment = id =>
  unwrap(api.get(BASE + '/' + encodeURIComponent(id) + '/attachment', cfg()))
export const adminAttachment = id =>
  unwrap(api.get(BASE + '/admin/questions/' + encodeURIComponent(id) + '/attachment', cfg()))

export const sendFeedback = (id, { helpful, reason }) =>
  unwrap(api.post(BASE + '/' + encodeURIComponent(id) + '/feedback', {
    helpful: !!helpful,
    reason: reason ? String(reason).slice(0, 500) : null
  }, cfg()))

// ---- CONTRACT 2: tickets ("פניות"). Ticket id = root question id. ----

export const TICKETS_PAGE_SIZE = 20

// GET /tickets?state=open|closed&cursor=<opaque>&limit=20
export const listTickets = ({ state, cursor, limit } = {}) => {
  const params = { state: state === 'closed' ? 'closed' : 'open', limit: limit || TICKETS_PAGE_SIZE }
  if (cursor) params.cursor = cursor
  return unwrap(api.get(BASE + '/tickets', cfg({ params })))
}

// GET /:root_id/thread -> { ticket, questions } (404 for a child id).
export const getThread = rootId =>
  unwrap(api.get(BASE + '/' + encodeURIComponent(rootId) + '/thread', cfg()))

export const closeTicket = rootId =>
  unwrap(api.patch(BASE + '/' + encodeURIComponent(rootId) + '/close', {}, cfg()))

export const reopenTicket = rootId =>
  unwrap(api.patch(BASE + '/' + encodeURIComponent(rootId) + '/reopen', {}, cfg()))

// Ack the notification_version that was actually rendered.
export const markTicketRead = (rootId, notificationVersion) =>
  unwrap(api.patch(BASE + '/' + encodeURIComponent(rootId) + '/read', {
    notification_version: parseInt(notificationVersion, 10) || 0
  }, cfg()))

// ---- Supervisor view ("פניות העובדות"): tenant-wide reads; writes below. ----

export const ADMIN_STATES = ['open', 'closed', 'all']

// GET /admin/tickets?state=open|closed|all&admin_id=&cursor=&limit=20
export const adminTickets = ({ state, admin_id, cursor, limit } = {}) => {
  const params = {
    state: ADMIN_STATES.indexOf(state) !== -1 ? state : 'open',
    limit: limit || TICKETS_PAGE_SIZE
  }
  const adminId = parseInt(admin_id, 10)
  if (adminId > 0) params.admin_id = adminId
  if (cursor) params.cursor = cursor
  return unwrap(api.get(BASE + '/admin/tickets', cfg({ params })))
}

// GET /admin/:root_id/thread -> { ticket, questions, truncated? }. Never acks read.
export const adminThread = rootId =>
  unwrap(api.get(BASE + '/admin/' + encodeURIComponent(rootId) + '/thread', cfg()))

// ---- Supervisor writes (SUPERVISOR_SPEC "Endpoint contracts"). Every body
// carries a client-generated request_uuid; the caller keeps it across retries
// of the SAME payload. Gated on GET /config `supervisor_writes`.

export const NOTE_MAX = 4000
export const ANSWER_MAX = 350

const adminPath = (id, action) => BASE + '/admin/' + encodeURIComponent(id) + '/' + action
const revision = v => parseInt(v, 10) || 0

// POST /admin/:root_id/instruction {request_uuid, text, expected_question_id}
// -> 201 { ...action envelope, instruction_id, question_id (the re-answer job),
// re_answer_of, status: 'queued' }; identical replay -> 200 with the same ids.
// Private: the recruiter never sees the instruction, only the re-answer.
// (The old public /message endpoint now answers 409 instruction_flow_required.)
export const INSTRUCTION_MAX = NOTE_MAX
export const adminInstruction = (rootId, { request_uuid, text, expected_question_id }) =>
  unwrap(api.post(adminPath(rootId, 'instruction'), {
    request_uuid,
    text,
    expected_question_id: parseInt(expected_question_id, 10) || null
  }, cfg()))

// POST /admin/:id/approve {request_uuid, expected_revision, text?}.
// `text` omitted = approve the reviewed draft exactly (details + sources kept);
// present = replace the answer and drop details/sources server-side.
export const adminApprove = (id, { request_uuid, expected_revision, text }) => {
  const body = { request_uuid, expected_revision: revision(expected_revision) }
  if (text !== undefined && text !== null) body.text = text
  return unwrap(api.post(adminPath(id, 'approve'), body, cfg()))
}

// POST /admin/:id/answer {request_uuid, expected_revision, text}
export const adminAnswer = (id, { request_uuid, expected_revision, text }) =>
  unwrap(api.post(adminPath(id, 'answer'), { request_uuid, expected_revision: revision(expected_revision), text }, cfg()))

// POST /admin/:id/cancel {request_uuid, expected_revision}
export const adminCancel = (id, { request_uuid, expected_revision }) =>
  unwrap(api.post(adminPath(id, 'cancel'), { request_uuid, expected_revision: revision(expected_revision) }, cfg()))

const lifecycleBody = ({ request_uuid, expected_notification_version, expected_closed }) => ({
  request_uuid,
  expected_notification_version: revision(expected_notification_version),
  expected_closed: !!expected_closed
})

// PATCH /admin/:root_id/close | reopen {request_uuid, expected_notification_version, expected_closed}
export const adminClose = (rootId, params) =>
  unwrap(api.patch(adminPath(rootId, 'close'), lifecycleBody(params), cfg()))

export const adminReopen = (rootId, params) =>
  unwrap(api.patch(adminPath(rootId, 'reopen'), lifecycleBody(params), cfg()))

export const SUPERVISOR_MESSAGES = {
  stale: 'הפנייה התעדכנה. יש לעיין בעדכון לפני השליחה.',
  uuid_conflict: 'הפעולה כבר נשלחה עם תוכן אחר. יש לעיין בפנייה ולשלוח שוב.',
  transition_not_allowed: 'לא ניתן לבצע את הפעולה במצב הנוכחי של השאלה. הפנייה רועננה.',
  text_invalid: 'הטקסט ריק או לא תקין.',
  text_too_long: max => 'הטקסט ארוך מדי (עד ' + max + ' תווים).',
  draft_missing: 'אין טיוטה לאישור. אפשר לבחור ״ענה בשם המערכת״.',
  latest_question_changed: 'נוספה שאלה חדשה. יש לעיין בה לפני שליחת ההנחיה.',
  instruction_flow_required: 'שליחת הודעות ליועצת הוחלפה בהנחיה למערכת. הפנייה רועננה.',
  precondition: 'הנתונים בפנייה אינם עדכניים. הפנייה רועננה, יש לנסות שוב.',
  rate_limited: seconds => 'הגעת למגבלת הפעולות לשעה. אפשר לנסות שוב בעוד ' + seconds + ' שניות.',
  forbidden: 'אין לך הרשאה לבצע פעולה זו.',
  feature_off: 'פעולות ההנהלה כבויות כרגע.',
  not_found: 'הפנייה או השאלה לא נמצאו.',
  network: 'אין חיבור לשרת כרגע, הפעולה לא בוצעה. אפשר לנסות שוב.'
}

/**
 * Supervisor-write error -> { kind, status, code, message, retryAfterS?, current? }.
 * kind ∈ stale | uuid_conflict | transition_not_allowed | latest_question_changed |
 *        instruction_flow_required | text_invalid | text_too_long | draft_missing |
 *        precondition | rate_limited | forbidden | feature_off | not_found |
 *        unauthorized | network | other
 * `current` = {question, ticket} from a 409 body (stale_turn carries both).
 */
export const mapSupervisorError = (err, { maxChars } = {}) => {
  const response = err && err.response
  if (!response) return { kind: 'network', status: 0, message: SUPERVISOR_MESSAGES.network }
  const status = response.status
  const body = (response.data && typeof response.data === 'object') ? response.data : {}
  const code = body.error
  const base = { status, code }
  const current = {
    question: (body.question && typeof body.question === 'object') ? body.question : null,
    ticket: (body.ticket && typeof body.ticket === 'object') ? body.ticket : null
  }
  if (status === 401) return { ...base, kind: 'unauthorized', message: '' }
  if (status === 403) return { ...base, kind: 'forbidden', message: SUPERVISOR_MESSAGES.forbidden }
  if (status === 404) {
    return code === 'feature_off'
      ? { ...base, kind: 'feature_off', message: SUPERVISOR_MESSAGES.feature_off }
      : { ...base, kind: 'not_found', message: SUPERVISOR_MESSAGES.not_found }
  }
  if (status === 409) {
    if (code === 'stale_turn' || code === 'stale_ticket') return { ...base, kind: 'stale', current, message: SUPERVISOR_MESSAGES.stale }
    if (code === 'request_uuid_conflict') return { ...base, kind: 'uuid_conflict', message: SUPERVISOR_MESSAGES.uuid_conflict }
    if (code === 'transition_not_allowed') return { ...base, kind: 'transition_not_allowed', current, message: SUPERVISOR_MESSAGES.transition_not_allowed }
    if (code === 'latest_question_changed') return { ...base, kind: 'latest_question_changed', current, message: SUPERVISOR_MESSAGES.latest_question_changed }
    if (code === 'instruction_flow_required') return { ...base, kind: 'instruction_flow_required', current, message: SUPERVISOR_MESSAGES.instruction_flow_required }
    return { ...base, kind: 'stale', current, message: SUPERVISOR_MESSAGES.stale }
  }
  if (status === 422) {
    if (code === 'text_invalid') return { ...base, kind: 'text_invalid', message: SUPERVISOR_MESSAGES.text_invalid }
    if (code === 'text_too_long') return { ...base, kind: 'text_too_long', message: SUPERVISOR_MESSAGES.text_too_long(maxChars || ANSWER_MAX) }
    if (code === 'draft_missing') return { ...base, kind: 'draft_missing', message: SUPERVISOR_MESSAGES.draft_missing }
    if (code === 'expected_revision_invalid' || code === 'precondition_invalid') {
      return { ...base, kind: 'precondition', message: SUPERVISOR_MESSAGES.precondition }
    }
    return { ...base, kind: 'other', message: SUPERVISOR_MESSAGES.text_invalid }
  }
  if (status === 429) {
    const retryAfterS = Number(body.retry_after_s) > 0 ? Math.ceil(Number(body.retry_after_s)) : 60
    return { ...base, kind: 'rate_limited', retryAfterS, message: SUPERVISOR_MESSAGES.rate_limited(retryAfterS) }
  }
  if (status >= 500) return { ...base, kind: 'network', message: SUPERVISOR_MESSAGES.network }
  return { ...base, kind: 'other', message: SUPERVISOR_MESSAGES.network }
}

// ---- Supervisor Jira decisions (JIRA_DECISION_SPEC "Rails endpoints"). ----

export const JIRA_TEXT_MAX = 5000
export const JIRA_DECISION_ACTIONS = ['approve', 'reject', 'edit']

// GET /admin/:question_id/jira_summary -> { question_id, jira, cached, fetched_at }
export const jiraSummary = questionId =>
  unwrap(api.get(adminPath(questionId, 'jira_summary'), cfg()))

// POST /admin/jira_statuses {task_ids} -> { statuses: { task_id: {status_key, approval:{astra_pending}} | null } }
// Status tag per linked task on the supervisor list; null = unknown right now.
export const JIRA_STATUSES_MAX_IDS = 50
export const jiraStatuses = taskIds =>
  unwrap(api.post(BASE + '/admin/jira_statuses', { task_ids: taskIds.map(String) }, cfg())).then(data => (data && data.statuses) || {})

// 200 = terminal body; 202 = { state: 'pending', retry_after_s }. Normalised so
// callers branch on `state` only.
const decisionResult = res => {
  const data = (res && res.data && typeof res.data === 'object') ? res.data : {}
  if (res && res.status === 202) {
    const retry = Number(data.retry_after_s)
    return { ...data, state: 'pending', retry_after_s: retry > 0 ? retry : 2 }
  }
  return { ...data, state: data.state || 'succeeded' }
}

// POST /admin/:question_id/jira_decision
export const jiraDecision = (questionId, p) => {
  const body = {
    request_uuid: p.request_uuid,
    action: p.action,
    expected_revision: revision(p.expected_revision),
    expected_jira_task_id: String(p.expected_jira_task_id === undefined || p.expected_jira_task_id === null ? '' : p.expected_jira_task_id),
    draft_version: revision(p.draft_version),
    request_version: revision(p.request_version),
    approval_revision: revision(p.approval_revision),
    text: String(p.text || '')
  }
  return api.post(adminPath(questionId, 'jira_decision'), body, cfg()).then(decisionResult)
}

// GET /admin/:question_id/jira_decisions/:request_uuid -> 202 while pending, then the terminal response.
export const jiraDecisionStatus = (questionId, requestUuid) =>
  api.get(adminPath(questionId, 'jira_decisions') + '/' + encodeURIComponent(requestUuid), cfg()).then(decisionResult)

export const JIRA_MESSAGES = {
  stale: 'המשימה השתנתה מאז שפתחת אותה. יש לעיין בעדכון לפני שליחה מחדש.',
  uuid_conflict: 'ההחלטה כבר נשלחה עם תוכן אחר. יש לעיין במשימה ולשלוח שוב.',
  text_invalid: 'יש לכתוב הסבר לפני השליחה.',
  text_too_long: 'הטקסט ארוך מדי (עד ' + JIRA_TEXT_MAX + ' תווים).',
  precondition: 'הנתונים במשימה אינם עדכניים. המשימה רועננה, יש לנסות שוב.',
  not_linked: 'לשאלה זו אין משימה מקושרת בג׳ירה.',
  not_found: 'המשימה או השאלה לא נמצאו.',
  task_not_found: 'המשימה לא נמצאה בג׳ירה. ההחלטה לא בוצעה.',
  unauthorized: 'ההתחברות פגה. יש להתחבר מחדש ולשלוח שוב.',
  unconfirmed: 'לא ניתן לוודא שההחלטה התקבלה — בדקו בג׳ירה',
  not_sent: 'ההחלטה לא התקבלה. אפשר לשלוח שוב.',
  jira_unavailable: 'ג׳ירה אינה זמינה כרגע. ההחלטה לא נשלחה, אפשר לנסות שוב בעוד רגע.',
  jira_not_configured: 'החיבור לג׳ירה אינו מוגדר עדיין. ההחלטה לא נשלחה.',
  temporarily_unavailable: 'השירות אינו זמין כרגע. ההחלטה לא נשלחה, אפשר לנסות שוב בעוד רגע.',
  rate_limited: SUPERVISOR_MESSAGES.rate_limited,
  forbidden: SUPERVISOR_MESSAGES.forbidden,
  feature_off: 'החלטות על משימות בג׳ירה כבויות כרגע.'
}

const JIRA_STALE_CODES = ['stale_turn', 'jira_link_changed', 'stale_draft', 'stale_request', 'stale_approval', 'invalid_state', 'no_draft']

/**
 * Jira read/decision error -> { kind, status, code, message, uncertain, terminal, retryAfterS?, current? }.
 * `terminal`: Rails stored this outcome for the reserved request_uuid (body.state 'failed'
 * or an action_id) — a resend with the same uuid would only replay it, so the next
 * explicit submit needs a fresh uuid. Pre-reservation errors (e.g. 503 before
 * acceptance) carry neither and keep the uuid.
 * kind 'unconfirmed' (502 jira_unconfirmed): delivery could not be confirmed after
 * many attempts — neither success nor failure; keep the uuid and allow status checks.
 * kind ∈ stale | uuid_conflict | text_invalid | text_too_long | precondition | rate_limited |
 *        forbidden | feature_off | not_linked | not_found | unavailable | unauthorized | uncertain
 * `uncertain` (no response / unexpected 5xx) on a WRITE means it may have committed:
 * the caller must keep the uuid and confirm via the status endpoint, never report failure.
 * `current` = the current Jira summary carried by a 409 body (`jira`), when present.
 */
export const mapJiraError = err => {
  const response = err && err.response
  if (!response) return { kind: 'uncertain', status: 0, uncertain: true, message: JIRA_COPY.pending }
  const status = response.status
  const body = (response.data && typeof response.data === 'object') ? response.data : {}
  const code = body.error
  const terminal = body.state === 'failed' || (body.action_id !== undefined && body.action_id !== null)
  const base = { status, code, uncertain: false, terminal }
  if (code === 'jira_unconfirmed' || body.state === 'unconfirmed') {
    return { ...base, kind: 'unconfirmed', terminal: false, message: JIRA_MESSAGES.unconfirmed }
  }
  if (status === 401) return { ...base, kind: 'unauthorized', message: JIRA_MESSAGES.unauthorized }
  if (status === 403) return { ...base, kind: 'forbidden', message: JIRA_MESSAGES.forbidden }
  if (status === 404) {
    if (code === 'feature_off') return { ...base, kind: 'feature_off', message: JIRA_MESSAGES.feature_off }
    if (code === 'jira_not_linked') return { ...base, kind: 'not_linked', message: JIRA_MESSAGES.not_linked }
    if (code === 'jira_task_not_found') return { ...base, kind: 'not_found', message: JIRA_MESSAGES.task_not_found }
    return { ...base, kind: 'not_found', message: JIRA_MESSAGES.not_found }
  }
  if (status === 409) {
    if (code === 'request_uuid_conflict') return { ...base, kind: 'uuid_conflict', message: JIRA_MESSAGES.uuid_conflict }
    const current = (body.jira && typeof body.jira === 'object') ? body.jira : null
    return { ...base, kind: 'stale', current, message: JIRA_MESSAGES.stale, staleCode: JIRA_STALE_CODES.indexOf(code) !== -1 ? code : 'stale' }
  }
  if (status === 422) {
    if (code === 'text_invalid') return { ...base, kind: 'text_invalid', message: JIRA_MESSAGES.text_invalid }
    if (code === 'text_too_long') return { ...base, kind: 'text_too_long', message: JIRA_MESSAGES.text_too_long }
    return { ...base, kind: 'precondition', message: JIRA_MESSAGES.precondition }
  }
  if (status === 429) {
    const retryAfterS = Number(body.retry_after_s) > 0 ? Math.ceil(Number(body.retry_after_s)) : 60
    return { ...base, kind: 'rate_limited', retryAfterS, message: JIRA_MESSAGES.rate_limited(retryAfterS) }
  }
  if (status === 502 && code === 'jira_unavailable') return { ...base, kind: 'unavailable', message: JIRA_MESSAGES.jira_unavailable }
  if (status === 503 && (code === 'jira_not_configured' || code === 'temporarily_unavailable')) {
    return { ...base, kind: 'unavailable', message: JIRA_MESSAGES[code] }
  }
  if (status >= 500) return { ...base, kind: 'uncertain', uncertain: true, message: JIRA_COPY.pending }
  return { ...base, kind: 'precondition', message: JIRA_MESSAGES.precondition }
}

export default {
  adminTickets,
  adminThread,
  adminInstruction,
  adminApprove,
  adminAnswer,
  adminCancel,
  adminClose,
  adminReopen,
  jiraSummary,
  jiraStatuses,
  jiraDecision,
  jiraDecisionStatus,
  listTickets,
  getThread,
  closeTicket,
  reopenTicket,
  markTicketRead,
  getConfig,
  getContext,
  listQuestions,
  getUpdates,
  getQuestion,
  createQuestion,
  sendFeedback
}
